<script lang="ts">
  // Version comparison view per INTERFACE-SPECIFICATION.md §9.2.
  //
  // Fetches two cube snapshots by hash from the read-path Worker
  // (?hash= query), computes per-cell deltas, and renders the §9.3
  // filter options. Partial rollback selects cells to revert and
  // creates a proposal pre-populated with the historical values.

  import { onMount } from 'svelte';
  import type {
    Jurisdiction,
    ProductId,
    ProductCatalogueFile,
    CubeCellValue
  } from '../lib/types';
  import type { LoanPurpose } from '../lib/staged-edits';
  import { fetchCatalogue, catalogueState } from '../lib/catalogue-store.svelte';
  import { projectMarketingBand, PUBLIC_BANDS } from '../lib/surface-projector';
  import { formatRate, formatRateDelta, formatAmount, formatAmountDelta, deltaCssClass } from '../lib/formatters';
  import { identity } from '../lib/identity.svelte';
  import { holdsAllRoles, requiredRolesForRateCubeRow } from '../lib/roles';
  import {
    diffAmountRange,
    diffPurposes,
    diffFees
  } from '../lib/proposal-preview-helpers';
  import SwipeToConfirm from './SwipeToConfirm.svelte';

  // Discriminated payload the History.svelte route's startRollback
  // dispatches to the right value-type's stage* mutators. Rates keeps
  // its existing per-cell shape so the rate-cube partial-rollback
  // selection still works; non-rate types roll back as a single
  // value-type block (the change-line invariant per spec §1.4 means
  // a draft holds one value type at a time anyway).
  //
  // The non-rate variants carry the *right-side* snapshot's value as
  // `before` so the staged-edits store has an honest baseline for its
  // SPA-side diff render. Without this the SubmitForReview preview
  // showed "undefined → $left" for purposes / amounts / fees because
  // the History dispatch synthesised an empty sentinel `before`.
  export type HistoryAmountRollback = {
    field: 'min' | 'max' | 'increment';
    before: number;
    new_value: number;
  };
  export type HistoryFeeRollback = {
    path: string;
    before: unknown;
    new_value: unknown;
  };
  export type HistoryPurposeRollback =
    | { action: 'add'; purpose: LoanPurpose }
    | { action: 'remove'; purpose_id: string; before: LoanPurpose }
    | { action: 'update'; purpose_id: string; before: LoanPurpose; purpose: LoanPurpose };

  export type HistoryRollbackPayload =
    | { value_type: 'rates'; cells: Array<{ coordinates: string; new_value: Partial<CubeCellValue> }> }
    | { value_type: 'amounts'; edits: HistoryAmountRollback[] }
    | { value_type: 'fees'; edits: HistoryFeeRollback[] }
    | { value_type: 'purposes'; edits: HistoryPurposeRollback[] };

  interface Props {
    jurisdiction: Jurisdiction;
    product: ProductId;
    leftHash: string;
    rightHash: string;
    onRollback: (payload: HistoryRollbackPayload) => void;
  }

  const { jurisdiction, product, leftHash, rightHash, onRollback }: Props = $props();

  let left = $state<ProductCatalogueFile | null>(null);
  let right = $state<ProductCatalogueFile | null>(null);
  let loading = $state<boolean>(true);
  let filter = $state<'all' | 'changed'>('changed');
  let selected = $state<Record<string, boolean>>({});

  const leftError = $derived(catalogueState.catalogueErrors[`${jurisdiction}/${product}@${leftHash}`]);
  const rightError = $derived(catalogueState.catalogueErrors[`${jurisdiction}/${product}@${rightHash}`]);

  onMount(() => {
    void (async () => {
      const [l, r] = await Promise.all([
        fetchCatalogue(jurisdiction, product, leftHash),
        fetchCatalogue(jurisdiction, product, rightHash)
      ]);
      left = l;
      right = r;
      loading = false;
    })();
  });

  interface DeltaRow {
    coordinates: string;
    leftRate: number | null;
    rightRate: number | null;
    leftAmount: number | null;
    rightAmount: number | null;
    rateDelta: number | null;
    amountDelta: number | null;
  }

  function deltaRows(): DeltaRow[] {
    if (!left || !right) return [];
    const rows: DeltaRow[] = [];
    const pricingModel = left._dimension_refs?.pricing_model;
    if (pricingModel === 'bureau_band_x_housing_x_channel') {
      const cube = left.rate_cube;
      for (const bandId of Object.keys(cube)) {
        for (const housingId of Object.keys(cube[bandId] ?? {})) {
          for (const channelId of Object.keys(cube[bandId]?.[housingId] ?? {})) {
            const leftCell = left.rate_cube[bandId]?.[housingId]?.[channelId];
            const rightCell = right.rate_cube[bandId]?.[housingId]?.[channelId];
            const coords = bandId + ' / ' + housingId + ' / ' + channelId;
            const lr = leftCell?.interest_rate_pa ?? null;
            const rr = rightCell?.interest_rate_pa ?? null;
            const la = leftCell?.max_loan_amount ?? null;
            const ra = rightCell?.max_loan_amount ?? null;
            const rateDelta = lr !== null && rr !== null ? rr - lr : null;
            const amountDelta = la !== null && ra !== null ? ra - la : null;
            if (filter === 'changed' && rateDelta === 0 && amountDelta === 0) continue;
            rows.push({ coordinates: coords, leftRate: lr, rightRate: rr, leftAmount: la, rightAmount: ra, rateDelta, amountDelta });
          }
        }
      }
    } else if (pricingModel === 'grade_x_subgrade_x_channel') {
      const cube = left.rate_cube as unknown as Record<string, Record<string, CubeCellValue>>;
      for (const cellId of Object.keys(cube)) {
        for (const channelId of Object.keys(cube[cellId] ?? {})) {
          const leftCell = (left.rate_cube as unknown as Record<string, Record<string, CubeCellValue>>)[cellId]?.[channelId];
          const rightCell = (right.rate_cube as unknown as Record<string, Record<string, CubeCellValue>>)[cellId]?.[channelId];
          const coords = cellId + ' / ' + channelId;
          const lr = leftCell?.interest_rate_pa ?? null;
          const rr = rightCell?.interest_rate_pa ?? null;
          const la = leftCell?.max_loan_amount ?? null;
          const ra = rightCell?.max_loan_amount ?? null;
          const rateDelta = lr !== null && rr !== null ? rr - lr : null;
          const amountDelta = la !== null && ra !== null ? ra - la : null;
          if (filter === 'changed' && rateDelta === 0 && amountDelta === 0) continue;
          rows.push({ coordinates: coords, leftRate: lr, rightRate: rr, leftAmount: la, rightAmount: ra, rateDelta, amountDelta });
        }
      }
    }
    return rows;
  }

  const rows = $derived(deltaRows());

  // Per-row required-roles. A row contributes a role only for fields
  // whose delta is non-zero — rolling back a row that only changed the
  // rate does not require proposer:amounts even though the row
  // describes a rate-cube cell that has both fields.
  interface GatedRow {
    row: DeltaRow;
    requiredRoles: readonly string[];
    canPropose: boolean;
  }
  const gatedRows = $derived<GatedRow[]>(rows.map((row) => {
    const required = requiredRolesForRateCubeRow({ rateDelta: row.rateDelta, amountDelta: row.amountDelta });
    return {
      row,
      requiredRoles: required,
      canPropose: holdsAllRoles(identity.roles, required)
    };
  }));
  const selectableRows = $derived(gatedRows.filter((g) => g.canPropose));
  const selectedRows = $derived(selectableRows.filter((g) => selected[g.row.coordinates]));
  const allRowsSelectable = $derived(gatedRows.length > 0 && gatedRows.every((g) => g.canPropose));
  const anyRowsSelectable = $derived(selectableRows.length > 0);

  function selectAll() {
    // Only tick rows the current identity can actually propose. A
    // row outside the identity's roles cannot contribute to a
    // partial rollback regardless of selection state — keeping it
    // untickable mirrors the per-row gate below the table.
    const next: Record<string, boolean> = {};
    for (const g of selectableRows) next[g.row.coordinates] = true;
    selected = next;
  }
  function clear() {
    selected = {};
  }

  function bandLabel(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }

  function rolesPhrase(roles: readonly string[]): string {
    if (roles.length === 0) return '';
    if (roles.length === 1) return roles[0]!;
    return roles.slice(0, -1).join(', ') + ' and ' + roles[roles.length - 1];
  }

  function disabledRowTitle(roles: readonly string[]): string {
    return `Requires ${rolesPhrase(roles)} — your identity does not hold ${roles.length > 1 ? 'all of these' : 'this role'}.`;
  }

  function rollbackSelected() {
    if (selectedRows.length === 0) return;
    const cells = selectedRows.map((g) => ({
      coordinates: g.row.coordinates,
      new_value: {
        interest_rate_pa: g.row.leftRate,
        max_loan_amount: g.row.leftAmount
      } as Partial<CubeCellValue>
    }));
    onRollback({ value_type: 'rates', cells });
  }

  function rollbackAll() {
    // Full-rollback is all-or-nothing by definition. The button is
    // hidden when allRowsSelectable is false; this guard is a
    // defence-in-depth against a click reaching the handler before
    // the derived state has refreshed.
    if (!allRowsSelectable) return;
    const cells = rows.map((r) => ({
      coordinates: r.coordinates,
      new_value: {
        interest_rate_pa: r.leftRate,
        max_loan_amount: r.leftAmount
      } as Partial<CubeCellValue>
    }));
    onRollback({ value_type: 'rates', cells });
  }

  // Non-rate diffs. Each is the difference between left (older) and
  // right (newer) snapshots; rolling back means proposing the LEFT
  // value as the new state. We only render a section when at least
  // one change is present on that value-type, to keep the page from
  // showing four empty headings when only rates moved.
  const amountChanges = $derived(
    left && right
      ? diffAmountRange(left, right).filter((r) => r.changed)
      : []
  );
  const purposeChanges = $derived(left && right ? diffPurposes(left, right) : []);
  const feeChanges = $derived(left && right ? diffFees(left, right) : []);

  // Role gates for the non-rate rollback buttons. Hidden unless the
  // current identity holds the matching proposer role; the
  // server-side requireRole at saveDraft / promoteDraftToProposed is
  // the authoritative gate, this just keeps the affordance honest.
  const canProposeAmounts = $derived(identity.affordances.canProposeAmounts);
  const canProposeFees = $derived(identity.affordances.canProposeFees);
  const canProposePurposes = $derived(identity.affordances.canProposePurposes);

  // Each non-rate rollback inverts the diff: proposing the LEFT value
  // as the new state. For purposes specifically, 'remove' on the
  // right side rolls back to an 'add' (restore the removed purpose);
  // 'add' on the right rolls back to a 'remove' (delete the newly-
  // added purpose); 'update' rolls back to an 'update' that restores
  // the prior value. Each payload carries the right-side snapshot's
  // value as `before` so the staged-edits store renders the diff
  // honestly in the submit-form preview.
  function rollbackAmounts() {
    const edits: HistoryAmountRollback[] = amountChanges.map((r) => ({
      field: r.field,
      before: r.after ?? 0,
      new_value: r.before as number
    }));
    if (edits.length === 0) return;
    onRollback({ value_type: 'amounts', edits });
  }

  function rollbackFees() {
    const edits: HistoryFeeRollback[] = feeChanges.map((d) => ({
      path: 'fees.' + d.path,
      before: d.after,
      new_value: d.before
    }));
    if (edits.length === 0) return;
    onRollback({ value_type: 'fees', edits });
  }

  function rollbackPurposes() {
    const edits: HistoryPurposeRollback[] = purposeChanges
      .map((d): HistoryPurposeRollback | null => {
        if (d.action === 'add' && d.after) {
          // Right has a purpose left does not — rolling back means
          // removing the addition. The store's StagedPurposeEdit for
          // 'remove' needs the before-value so the SPA can render
          // "Removed: <display_name>" in the submit preview.
          return { action: 'remove', purpose_id: d.purpose_id, before: d.after as LoanPurpose };
        }
        if (d.action === 'remove' && d.before) {
          // Right is missing a purpose left has — rolling back means
          // re-adding the removed purpose.
          return { action: 'add', purpose: d.before as LoanPurpose };
        }
        if (d.action === 'update' && d.before && d.after) {
          // The user's intent is "make the catalogue look like left
          // again". Store before = right-side value (what the user is
          // mutating FROM), staged = left-side value (the target).
          return { action: 'update', purpose_id: d.purpose_id, before: d.after as LoanPurpose, purpose: d.before as LoanPurpose };
        }
        return null;
      })
      .filter((e): e is HistoryPurposeRollback => e !== null);
    if (edits.length === 0) return;
    onRollback({ value_type: 'purposes', edits });
  }

  const marketingEffect = $derived(
    left && right
      ? PUBLIC_BANDS.map((band) => {
          const l = projectMarketingBand(left!, band);
          const r = projectMarketingBand(right!, band);
          return { band, leftMin: l.min_rate, rightMin: r.min_rate, delta: l.min_rate !== null && r.min_rate !== null ? Number(r.min_rate) - Number(l.min_rate) : null };
        })
      : []
  );
</script>

<section>
  {#if loading}
    <p class="muted">Loading comparison snapshots…</p>
  {:else if !left || !right}
    <div class="banner banner-fail">
      <strong>Could not load one or both catalogue snapshots.</strong>
      <ul class="snapshot-error-list">
        {#if !left}
          <li>
            <code>{leftHash.slice(0, 8)}</code>:
            {leftError ?? 'snapshot not returned'}
          </li>
        {/if}
        {#if !right}
          <li>
            <code>{rightHash.slice(0, 8)}</code>:
            {rightError ?? 'snapshot not returned'}
          </li>
        {/if}
      </ul>
      <p class="muted">
        The deploy workflow currently writes only the in-effect version's cube
        to KV. Comparing historical or future-dated versions requires
        extending <code>deploy.yml</code> to write every manifest entry's
        cube — slated for PR 4 or PR 5 alongside the audit-trail tool.
      </p>
    </div>
  {:else}
    <h3 class="section-heading">Comparing <code>{leftHash.slice(0, 8)}</code> → <code>{rightHash.slice(0, 8)}</code></h3>

    <h3 class="section-heading">Marketing-surface effect</h3>
    <div class="table-scroll">
    <table class="cube-table">
      <thead>
        <tr>
          <th>Public band</th>
          <th class="num">% min (left)</th>
          <th class="num">% min (right)</th>
          <th class="num">Δ %</th>
        </tr>
      </thead>
      <tbody>
        {#each marketingEffect as row (row.band)}
          <tr>
            <td>{bandLabel(row.band)}</td>
            <td class="num">{formatRate(row.leftMin)}</td>
            <td class="num">{formatRate(row.rightMin)}</td>
            <td class="num {deltaCssClass(row.delta)}">{formatRateDelta(row.delta)}</td>
          </tr>
        {/each}
      </tbody>
    </table>
    </div>

    <h3 class="section-heading">Cube cell deltas</h3>
    <div class="filter-controls">
      <label><input type="radio" name="filter" value="changed" checked={filter === 'changed'} onchange={() => (filter = 'changed')} /> Changed only</label>
      <label><input type="radio" name="filter" value="all" checked={filter === 'all'} onchange={() => (filter = 'all')} /> All cells</label>
      <button class="btn" type="button" onclick={selectAll}>Select all</button>
      <button class="btn" type="button" onclick={clear}>Clear</button>
    </div>
    <div class="table-scroll">
    <table class="cube-table">
      <thead>
        <tr>
          <th></th>
          <th>Coordinates</th>
          <th class="num">% left</th>
          <th class="num">% right</th>
          <th class="num">Δ %</th>
          <th class="num">$ left</th>
          <th class="num">$ right</th>
          <th class="num">Δ $</th>
        </tr>
      </thead>
      <tbody>
        {#each gatedRows as g (g.row.coordinates)}
          <tr class={g.canPropose ? '' : 'row-gated'}>
            <td>
              <input
                type="checkbox"
                bind:checked={selected[g.row.coordinates]}
                disabled={!g.canPropose}
                title={g.canPropose ? undefined : disabledRowTitle(g.requiredRoles)}
                aria-label="Select {g.row.coordinates}"
              />
            </td>
            <td><code>{g.row.coordinates}</code></td>
            <td class="num">{formatRate(g.row.leftRate)}</td>
            <td class="num">{formatRate(g.row.rightRate)}</td>
            <td class="num {deltaCssClass(g.row.rateDelta)}">{formatRateDelta(g.row.rateDelta)}</td>
            <td class="num">{formatAmount(g.row.leftAmount)}</td>
            <td class="num">{formatAmount(g.row.rightAmount)}</td>
            <td class="num {deltaCssClass(g.row.amountDelta)}">{formatAmountDelta(g.row.amountDelta)}</td>
          </tr>
        {/each}
      </tbody>
    </table>
    </div>

    <footer class="comparison-footer">
      {#if anyRowsSelectable}
        <button class="btn" type="button" onclick={rollbackSelected} disabled={selectedRows.length === 0}>
          {#if selectableRows.length < gatedRows.length}
            Roll back {selectedRows.length} of {selectableRows.length} selectable cell{selectableRows.length === 1 ? '' : 's'} to left (partial)
          {:else}
            Roll back {selectedRows.length} selected cell{selectedRows.length === 1 ? '' : 's'} to left (partial)
          {/if}
        </button>
      {/if}
      {#if allRowsSelectable}
        <div class="desktop-only">
          <button class="btn btn-primary" type="button" onclick={rollbackAll}>
            Roll back all {rows.length} cells (full)
          </button>
        </div>
        <div class="mobile-only comparison-footer__full">
          <SwipeToConfirm
            label="Roll back all {rows.length} cells (full)"
            confirmingLabel="Rolling back…"
            onConfirm={rollbackAll}
          />
        </div>
      {/if}
      {#if !anyRowsSelectable && gatedRows.length > 0}
        <p class="muted">
          Your identity does not hold the proposer roles required for any of the changed cells.
          Rollback is not available from this view; a user holding the required role must initiate it.
        </p>
      {/if}
    </footer>

    {#if amountChanges.length > 0}
      <h3 class="section-heading">Loan amount range changes</h3>
      <div class="table-scroll">
        <table class="cube-table">
          <thead>
            <tr>
              <th>Field</th>
              <th class="num">$ left</th>
              <th class="num">$ right</th>
              <th class="num">Δ $</th>
            </tr>
          </thead>
          <tbody>
            {#each amountChanges as row (row.field)}
              <tr>
                <td><strong>{row.field === 'min' ? 'Minimum' : row.field === 'max' ? 'Maximum' : 'Increment'}</strong></td>
                <td class="num">{row.before !== undefined ? '$' + formatAmount(row.before) : '—'}</td>
                <td class="num">{row.after !== undefined ? '$' + formatAmount(row.after) : '—'}</td>
                <td class="num {deltaCssClass(row.delta)}">{row.delta !== null ? (row.delta > 0 ? '+' : '') + formatAmount(row.delta) : '—'}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      <footer class="comparison-footer">
        {#if canProposeAmounts}
          <button class="btn" type="button" onclick={rollbackAmounts}>
            Roll back {amountChanges.length} amount-range change{amountChanges.length === 1 ? '' : 's'} to left
          </button>
        {:else}
          <p class="muted">Your identity does not hold proposer:amounts; rollback of amount-range changes is hidden.</p>
        {/if}
      </footer>
    {/if}

    {#if feeChanges.length > 0}
      <h3 class="section-heading">Fee schedule changes</h3>
      <div class="table-scroll">
        <table class="cube-table">
          <thead>
            <tr>
              <th>Path</th>
              <th>Left</th>
              <th>Right</th>
            </tr>
          </thead>
          <tbody>
            {#each feeChanges as row (row.path)}
              <tr>
                <td><code>{row.path}</code></td>
                <td><code class="diff-payload">{JSON.stringify(row.before)}</code></td>
                <td><code class="diff-payload">{JSON.stringify(row.after)}</code></td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      <footer class="comparison-footer">
        {#if canProposeFees}
          <button class="btn" type="button" onclick={rollbackFees}>
            Roll back {feeChanges.length} fee path{feeChanges.length === 1 ? '' : 's'} to left
          </button>
        {:else}
          <p class="muted">Your identity does not hold proposer:fees; rollback of fee changes is hidden.</p>
        {/if}
      </footer>
    {/if}

    {#if purposeChanges.length > 0}
      <h3 class="section-heading">Loan purpose changes</h3>
      <div class="table-scroll">
        <table class="cube-table">
          <thead>
            <tr>
              <th>Action (right relative to left)</th>
              <th>ID</th>
              <th>Left</th>
              <th>Right</th>
            </tr>
          </thead>
          <tbody>
            {#each purposeChanges as row (row.purpose_id)}
              <tr>
                <td><strong>{row.action.charAt(0).toUpperCase() + row.action.slice(1)}</strong></td>
                <td><code>{row.purpose_id}</code></td>
                <td>
                  {#if row.before}
                    {row.before.display_name}
                    {#if row.before.amount_range}
                      <span class="muted"> · ${formatAmount(row.before.amount_range.min)}–${formatAmount(row.before.amount_range.max)}</span>
                    {/if}
                  {:else}
                    <span class="muted">—</span>
                  {/if}
                </td>
                <td>
                  {#if row.after}
                    {row.after.display_name}
                    {#if row.after.amount_range}
                      <span class="muted"> · ${formatAmount(row.after.amount_range.min)}–${formatAmount(row.after.amount_range.max)}</span>
                    {/if}
                  {:else}
                    <span class="muted">—</span>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      <footer class="comparison-footer">
        {#if canProposePurposes}
          <button class="btn" type="button" onclick={rollbackPurposes}>
            Roll back {purposeChanges.length} purpose change{purposeChanges.length === 1 ? '' : 's'} to left
          </button>
        {:else}
          <p class="muted">Your identity does not hold proposer:purposes; rollback of purpose changes is hidden.</p>
        {/if}
      </footer>
    {/if}
  {/if}
</section>

<style>
  .filter-controls {
    display: flex;
    gap: var(--space-3);
    align-items: center;
    margin: var(--space-3) 0;
    flex-wrap: wrap;
  }
  .filter-controls label { font-size: var(--type-sm); }
  .snapshot-error-list {
    list-style: none;
    padding: 0;
    margin: var(--space-2) 0;
    font-family: var(--font-mono);
    font-size: var(--type-sm);
  }
  .snapshot-error-list li {
    padding: var(--space-1) 0;
  }
  .comparison-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--space-3);
    margin-top: var(--space-4);
    padding-top: var(--space-3);
    border-top: 1px solid var(--surface-line-soft);
  }
  .comparison-footer .muted {
    margin: 0;
    flex: 1;
    text-align: right;
    font-size: var(--type-sm);
  }
  .row-gated {
    opacity: 0.6;
  }
  code { font-family: var(--font-mono); }
  /* Same wrap-aggressively style as ReviewInterface for JSON payloads
     so a wide tiers array does not blow out the table. */
  code.diff-payload {
    display: inline-block;
    max-width: 24rem;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: var(--type-xs);
  }
</style>
